'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef } from 'react';

import Reveal from "@/components/animation/reveal";
import Cta from "@/components/cta/cta";
import Tree from "@/components/icon/tree";
import TreeImages from "@/components/icon/tree-images";
import Section from "@/components/section";
import { useApp } from "@/context/AppContext";

export default function AboutIntro({ link, text, title }) {
  const { isMobile } = useApp();
  const treeWrapperRef = useRef(null);
  const treeRef = useRef(null);
  const treeImagesRef = useRef(null);
  const animationsRef = useRef([]);

  const getVisiblePaths = () => {
    const svg = isMobile
      ? treeRef.current.querySelector('svg:first-child')
      : treeRef.current.querySelector('svg:last-child');
    return svg.querySelectorAll('.tree-path');
  };

  const getVisibleImages = () => {
    const svg = isMobile
      ? treeImagesRef.current.querySelector('svg:last-child')
      : treeImagesRef.current.querySelector('svg:first-child');
    return svg.querySelectorAll('.tree-image');
  };

  const enter = () => {
    // Kill any existing animations
    animationsRef.current.forEach(tween => tween.kill());
    animationsRef.current = [];

    const paths = getVisiblePaths();
    const images = getVisibleImages();

    // Sort paths by their top Y position (top to bottom)
    const sortedPaths = Array.from(paths).sort((a, b) => {
      return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
    });

    // Animate paths drawing downward with stagger
    sortedPaths.forEach((path, index) => {
      const length = path.getTotalLength();
      const tween = gsap.fromTo(path, {
        strokeDashoffset: length,
      }, {
        strokeDashoffset: length * 2,
        duration: 1,
        ease: 'power2.out',
        delay: index * 0.15,
      });
      animationsRef.current.push(tween);
    });

    // Manual delays for each image
    const imageDelays = isMobile
      ? [1.5, 1.1, 1.3, 1.4, 0.9, 1.2, 0.2, 0.4] // Mobile: 8 images
      : [1.1, 1.2, 2.6, 1.3, 1.2, 2, 2.6, 3, 2.4, 2.5, 1.7, 1.3, 0.8, 1.7, 0.8, 0.5, 2.2]; // Desktop: 17 images

    // Animate images fading in with manual delays
    images.forEach((image, index) => {
      const tween = gsap.fromTo(image, {
        opacity: 0,
      }, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
        delay: imageDelays[index] ?? 0.5 + index * 0.15, // Fallback to stagger if not defined
      });
      animationsRef.current.push(tween);
    });
  };

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      // Set initial state - paths hidden using actual path length
      const paths = getVisiblePaths();
      paths.forEach((path) => {
        const length = path.getTotalLength();
        gsap.set(path, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });
      });

      // Set initial state - images hidden
      const images = getVisibleImages();
      gsap.set(images, { opacity: 0 });

      ScrollTrigger.create({
        trigger: treeWrapperRef.current,
        start: 'top bottom',
        onEnter: enter,
      });
    });

    return () => {
      animationsRef.current.forEach(tween => tween.kill());
      animationsRef.current = [];
    };
  }, [isMobile]);

  return (
    <Section theme="main-light" className="pt-20.25 pb-16 lg:pt-21 lg:pb-23.5 flex flex-col items-center">
      <div className="grid-7">
        <Reveal className="col-span-full max-w-130 mx-auto lg:max-w-none">
          <h2 className="heading-3-small text-center text-pretty lg:heading-3 lg:max-w-[67%] lg:mx-auto">{title}</h2>
          <p className="mx-auto mt-8 text-center heading-5-small text-pretty xl:heading-5 lg:mt-10 lg:max-w-[50%]">{text}</p>
          <Cta href={link.href} className="w-fit mt-8 mx-auto cta-big lg:mt-7.5">{link.label}</Cta>
        </Reveal>

        <div ref={treeWrapperRef} className="relative col-span-full mt-2 lg:mt-8.5 before:content-[''] before:absolute before:z-1 before:top-0 before:left-0 before:w-full before:h-24 before:bg-gradient-to-b before:from-main-light before:to-transparent lg:before:h-70">
          <Tree ref={treeRef} className="relative top-0 -left-6 w-[calc(100%+3rem)] lg:w-full lg:left-0" aria-hidden="true" />
          <TreeImages ref={treeImagesRef} className="absolute top-0 -left-6 w-[calc(100%+3rem)] lg:w-full lg:left-0" aria-hidden="true" />
        </div>
      </div>
    </Section>
  )
}
